// QR Kiosk v2 — ONE keyboard-safe bottom-sheet shell (Round-6, §1.5). Promotes the
// former per-file SheetShell/inline-Dialog recipe into a single portaled, keyboard-safe
// component. Every kiosk bottom sheet (task note / hold / done, confirm, report-detail
// delete) adopts this so its action buttons — the last in-flow row — always ride above
// the on-screen keyboard.
//
// Geometry (§1.4 pattern S): portal into the kiosk root (token scope + escapes animated
// subtrees), `bottom: keyboardInset` (lift the whole sheet above the keyboard) and
// `maxHeight: calc(100dvh - (keyboardInset + 56)px)` + `overflowY: auto` so tall content
// scrolls INSTEAD of pushing the buttons off-screen. Entrance = kv2-sheet-up (safe: a
// sheet holds no fixed descendants, and the keyframe fill is now `backwards`).
import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import { useKioskOverlayRoot } from "./kioskOverlayRoot";
import { useKeyboardInset } from "./useKeyboardInset";

export interface KioskSheetProps {
  open: boolean;
  /** Called on any dismiss (Escape / overlay tap). In-flight guards live in the caller. */
  onRequestClose: () => void;
  /** Optional sheet title. When provided, renders the standard Dialog.Title header row.
   *  Omit it ONLY when the children supply their own Dialog.Title (a11y requirement). */
  title?: string;
  overlayZIndex?: number; // default 40; ConfirmSheet-above-carousel passes 1410
  contentZIndex?: number; // default 41; …1411
  children: React.ReactNode;
}

export function KioskSheet({
  open,
  onRequestClose,
  title,
  overlayZIndex = 40,
  contentZIndex = 41,
  children,
}: KioskSheetProps) {
  const overlayRoot = useKioskOverlayRoot();
  const { isPhone, isTablet } = useBreakpoint();
  const { keyboardInset } = useKeyboardInset();
  const columnMaxWidth: number | string = isPhone ? "100%" : isTablet ? 560 : 640;

  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next) onRequestClose(); }}>
      <Dialog.Portal container={overlayRoot}>
        <Dialog.Overlay
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: overlayZIndex }}
        />
        <Dialog.Content
          aria-describedby={undefined}
          className="kv2-sheet-up"
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: keyboardInset,
            zIndex: contentZIndex,
            maxWidth: columnMaxWidth,
            margin: "0 auto",
            maxHeight: `calc(100dvh - ${keyboardInset + 56}px)`,
            overflowY: "auto",
            background: "var(--card)",
            borderTop: "1px solid var(--border)",
            borderRadius: "16px 16px 0 0",
            padding: "18px 16px calc(16px + env(safe-area-inset-bottom, 0px))",
            boxShadow: "var(--shadow)",
          }}
        >
          {title !== undefined && (
            <Dialog.Title asChild>
              <div style={{ fontSize: 15.5, fontWeight: 700, color: "var(--ink)" }}>{title}</div>
            </Dialog.Title>
          )}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default KioskSheet;
